import { FAMILY } from '@app/shared'
import { plain } from '@app/stationery'
import {
  isNoteLine,
  labelWithPlace,
  scheduledDays,
  sheetTitleHe,
  withSunset,
} from './layout'
import { type ScheduleSegmentKey, SEGMENT_LABEL, segmentDays } from './segments'
import type { LuachDay, LuachEntry, LuachResult, LuachShul } from './types'

/**
 * The schedule as an HTML table, for the body of an email.
 *
 * A luach IS tabular — a label and a time are two columns, and the printed
 * sheet renders them that way. Flattening it into run-on lines is what made the
 * text version hard to read: nothing lines up, and every line has to survive
 * the bidirectional algorithm on its own. A table gets its structure from
 * layout instead of punctuation, and each cell is its own directional context
 * by definition, so the isolate characters the text version needs are not
 * required here at all.
 *
 * Deliberately NOT reusing `luach.css`. Mail clients strip `<style>` blocks
 * unpredictably and support neither flex nor grid, so this is table markup with
 * inlined declarations — a parallel renderer that has to be kept honest against
 * the sheet by hand. The times themselves are not: they come from the same
 * resolved result the sheet and the PDF read, so only the presentation is
 * duplicated, never the computation.
 */

/** Escape for HTML text. The times and labels are ours, but a shul's own
 *  greeting or label is free text and must not be able to inject markup. */
const esc = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

/** The printed value of a line — a composite joins its opinions. */
function timeText(e: LuachEntry): string {
  if (e.parts?.length) {
    return e.parts
      .map((p) => `${plain(p.labelHe)} ${p.time ?? ''}`.trim())
      .join(' / ')
  }
  return e.displayText ?? e.time ?? e.noteText ?? ''
}

const HEBREW_STACK = `'${FAMILY.sans}', 'Assistant', 'Arial Hebrew', Arial, sans-serif`

/**
 * A Latin stack for the figures.
 *
 * A time inherits neither the Hebrew face nor the RTL direction. Left to
 * inherit, `6:31 PM` laid out right-to-left and rendered PM-first, and the
 * digits fell through to whatever face the Hebrew font could not supply — so
 * the meridiem read larger than the number it qualifies.
 *
 * Proportional rather than monospace, though the app's own type scale reserves
 * a mono face for times: at 14px beside Plex Sans Hebrew, mono's wide digits
 * outweigh the Hebrew labels and the column reads as the loudest thing on the
 * sheet, which inverts the emphasis — the tefilla is the content, the time is
 * the datum. `tabular-nums` gives the aligned column that mono was there for,
 * so nothing is lost by dropping it.
 */
const FIGURE_STACK =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"

const S = {
  frame: `direction:rtl;text-align:right;font-family:${HEBREW_STACK};color:#18181b;`,
  shul: 'font-size:18px;font-weight:600;margin:0 0 2px;',
  occasion: 'font-size:15px;color:#52525b;margin:0 0 16px;',
  table: 'width:100%;border-collapse:collapse;margin:0 0 18px;',
  // The day heading spans both columns and carries the rule beneath it, so the
  // day reads as a section rather than as another row of the same list.
  dayCell:
    'padding:14px 0 6px;border-bottom:1px solid #d4d4d8;font-size:14px;font-weight:600;',
  dayDate: 'font-weight:400;color:#71717a;',
  label: 'padding:5px 0;font-size:14px;',
  /*
   * The time column: its own LTR direction so `6:31 PM` reads in that order
   * inside a right-to-left table, its own Latin face so the digits and the
   * meridiem share metrics, and tabular figures so the column aligns down the
   * page. Left-aligned, which in an RTL table puts it opposite the label.
   */
  time: `padding:5px 0;font-size:14px;direction:ltr;text-align:left;white-space:nowrap;font-family:${FIGURE_STACK};font-variant-numeric:tabular-nums;`,
  /* Matches the sheet's `.meridiem`: small, lowercase, muted, with the gap on
     its physical left — a time reads left-to-right whatever the sheet does. */
  meridiem: 'font-size:0.68em;color:#71717a;margin-left:3px;',
  /* A reminder spans the row and is centered, matching the sheet's `.row--note`:
     it is the same informational voice, and a message that styled it otherwise
     would not read as the sheet it copies. `--highlight` spelled literally —
     an email has no tokens. */
  note: 'padding:5px 0;font-size:13px;color:#8a6317;text-align:center;',
  /* שקיעה is a reference the day's times are measured from, not a davening —
     the sheet gives it the same centered informational voice as a note, and so
     does this. Its name and its time are one phrase, so neither half is set
     apart from the other. */
  anchor: 'padding:5px 0;font-size:13px;color:#8a6317;text-align:center;',
  greeting:
    'font-size:14px;color:#52525b;margin:16px 0 0;padding-top:12px;border-top:1px solid #e4e4e7;',
}

/**
 * A time with its meridiem set quiet, the way the printed sheet does it.
 *
 * The sheet's own `<Time>` splits the value and renders `pm` small and
 * lowercase, because the hour is the fact and the meridiem only qualifies it.
 * Rendering it at full size — as this did — makes the shortest, least
 * informative part of the line the loudest. Anything that is not a plain
 * `h:mm AM` (a composite's two opinions, a phrase like "אחר מנחה") passes
 * through untouched.
 */
function timeHtml(value: string): string {
  const m = /^(\d{1,2}:\d{2})\s*(AM|PM)$/i.exec(value.trim())
  if (!m) return esc(value)
  return `${esc(m[1])}<span style="${S.meridiem}">${esc(m[2].toLowerCase())}</span>`
}

export function scheduleToHtml(
  result: LuachResult,
  shul: LuachShul,
  segment?: ScheduleSegmentKey,
): string {
  const days: LuachDay[] = scheduledDays(
    segment ? segmentDays(result, segment) : result.days,
  )
  const occasion = segment
    ? `${plain(sheetTitleHe(result))} · ${SEGMENT_LABEL[segment].he}`
    : plain(sheetTitleHe(result))

  const rows: string[] = []
  for (const day of days) {
    rows.push(
      `<tr><td colspan="2" style="${S.dayCell}">${esc(plain(day.dayLabelHe))}` +
        ` <span style="${S.dayDate}">· ${esc(day.hebrewDateHe)}</span></td></tr>`,
    )
    for (const item of withSunset(day)) {
      /*
       * שקיעה, placed among the times it explains — one centered phrase, as the
       * sheet prints it. It comes from `withSunset` rather than from the day's
       * entries, so a renderer that walks `day.entries` directly drops the fact
       * every other time on the day is measured from.
       */
      if (item.kind === 'anchor') {
        rows.push(
          `<tr><td colspan="2" style="${S.anchor}">שקיעה ${timeHtml(item.time)}</td></tr>`,
        )
        continue
      }
      const e = item.entry
      if (e.emphasis === 'hidden') continue
      // A note is its own sentence across the row — never a label with the same
      // words repeated beside it.
      if (isNoteLine(e)) {
        rows.push(
          `<tr><td colspan="2" style="${S.note}">${esc(plain(e.noteText ?? ''))}</td></tr>`,
        )
        continue
      }
      const t = timeText(e)
      if (!t && !e.noteText) continue
      const label = esc(labelWithPlace(e))
      rows.push(
        t
          ? `<tr><td style="${S.label}">${label}</td><td style="${S.time}">${timeHtml(t)}</td></tr>`
          : `<tr><td colspan="2" style="${S.label}">${label}</td></tr>`,
      )
    }
  }

  return [
    `<div style="${S.frame}">`,
    `<p style="${S.shul}">${esc(plain(shul.nameHe || shul.name))}</p>`,
    `<p style="${S.occasion}">${esc(occasion)}</p>`,
    `<table role="presentation" style="${S.table}"><tbody>`,
    ...rows,
    '</tbody></table>',
    result.greetingHe
      ? `<p style="${S.greeting}">${esc(plain(result.greetingHe))}</p>`
      : '',
    '</div>',
  ]
    .filter(Boolean)
    .join('')
}
